import { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { normalizeFullWidthNumerics } from '../../../utils/numericInputNormalize';
import TppProofUploadButton from './TppProofUploadButton';
import {
  TPP_MODAL_CONTENT_CLASS,
  TppBalanceDeltaHint,
  TppBtnSpinner,
  TppDateField,
  TppOptionalMark,
  TppRequiredMark,
  isTppAmountInputReady,
  parseTppAmountInput,
  selectTppInputOnFocus,
  tppTodayIso,
} from './tppUiShared';
import { useTppOnModalFreshOpen } from './useTppOnModalFreshOpen';
import type { TppReturnSubmitPayload } from './tppOpsModalTypes';

type Props = {
  show: boolean;
  onHide: () => void;
  closeLabel: string;
  money: (n: number) => string;
  zeroBalanceLabel: string;
  balanceNumber: number | null;
  returnRouteSeed: string;
  pending: boolean;
  onSubmit: (payload: TppReturnSubmitPayload) => void;
};

export default function TppReturnModal({
  show,
  onHide,
  closeLabel,
  money,
  zeroBalanceLabel,
  balanceNumber,
  returnRouteSeed,
  pending,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const balance = balanceNumber ?? 0;
  const [inDate, setInDate] = useState(() => tppTodayIso());
  const [amount, setAmount] = useState('');
  const [route, setRoute] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [proofTypeRejected, setProofTypeRejected] = useState(false);

  useTppOnModalFreshOpen(show, () => {
    setInDate(tppTodayIso());
    setAmount('');
    setProof(null);
    setProofTypeRejected(false);
    setRoute(returnRouteSeed);
  });

  const isUnusedReturn = parseTppAmountInput(amount) === 0;
  const unusedRouteDefault = () => t('tpp.return_unused_route_default');
  /** 証跡画像は常に任意。利用ありのときは経路のみ必須 */
  const canSubmit =
    isTppAmountInputReady(amount) && (isUnusedReturn ? true : !!route.trim());

  /** 貸出時の経路が残ると「未使用」と矛盾するので、ワンクリックで経路も未使用既定へ */
  const fillUnusedZero = () => {
    setAmount('0');
    setRoute(unusedRouteDefault());
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable contentClassName={TPP_MODAL_CONTENT_CLASS}>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (!pending && canSubmit) {
            const routeOut =
              isUnusedReturn && !route.trim() ? unusedRouteDefault() : route;
            onSubmit({ inDate, amount, route: routeOut, proof });
          }
        }}
      >
        <Modal.Header closeButton closeLabel={closeLabel}>
          <Modal.Title>{t('tpp.return_modal_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3">
            {isUnusedReturn
              ? t('tpp.return_unused_required_hint', { zero: zeroBalanceLabel })
              : t('tpp.return_required_hint', { zero: zeroBalanceLabel })}
          </p>
          <TppDateField
            id="tpp-return-in-date"
            label={t('tpp.col_in')}
            value={inDate}
            onChange={setInDate}
          />
          <Form.Group className="mb-2">
            <Form.Label htmlFor="tpp-return-amount">
              {t('tpp.fare_amount')}
              <TppRequiredMark />
            </Form.Label>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <Form.Control
                id="tpp-return-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(normalizeFullWidthNumerics(e.target.value))}
                onFocus={selectTppInputOnFocus}
                required
                autoFocus
                className="tpp-amount-input"
                style={{ maxWidth: '12rem' }}
              />
              <Button
                type="button"
                size="sm"
                variant={isUnusedReturn ? 'success' : 'outline-secondary'}
                className="text-nowrap"
                onClick={fillUnusedZero}
              >
                {t('tpp.return_unused_zero', { zero: zeroBalanceLabel })}
              </Button>
            </div>
            {balanceNumber != null && (
              <>
                <Form.Text muted className="d-block mt-1">
                  {balance <= 0
                    ? t('tpp.borrow_balance_label', { balance: money(balance) })
                    : t('tpp.return_balance_available', { balance: money(balance) })}
                </Form.Text>
                {!isUnusedReturn ? (
                  <TppBalanceDeltaHint
                    mode="subtract"
                    amountRaw={amount}
                    balanceNumber={balance}
                    moneyLabel={money}
                    negativeHint={t('tpp.return_balance_after_short')}
                    afterTemplate={(b) =>
                      t('tpp.return_balance_after', { balance: b })
                    }
                  />
                ) : null}
              </>
            )}
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label htmlFor="tpp-return-route">
              {t('tpp.route')}
              {isUnusedReturn ? <TppOptionalMark /> : <TppRequiredMark />}
            </Form.Label>
            <Form.Control
              id="tpp-return-route"
              as="textarea"
              rows={2}
              value={route}
              onChange={(e) => setRoute(e.target.value)}
              required={!isUnusedReturn}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="d-flex align-items-center gap-1">
              <i className="fas fa-paperclip" aria-hidden />
              {t('tpp.proof_image')}
              <TppOptionalMark />
            </Form.Label>
            <div>
              <TppProofUploadButton
                hasProof={!!proof}
                pending={pending}
                label={t('tpp.return_proof_pick')}
                replaceLabel={t('tpp.return_proof_repick')}
                onFile={(f) => {
                  setProofTypeRejected(false);
                  setProof(f);
                }}
                onReject={() => {
                  setProof(null);
                  setProofTypeRejected(true);
                }}
              />
            </div>
            <Form.Text muted={!proofTypeRejected} className={`d-block${proofTypeRejected ? ' text-danger' : ''}`}>
              {proofTypeRejected
                ? t('tpp.proof_type_rejected')
                : proof
                  ? t('tpp.return_proof_selected', { name: proof.name })
                  : t('tpp.return_proof_hint')}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onHide}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={pending || !canSubmit}>
            <TppBtnSpinner show={pending} />
            {t('tpp.return_confirm')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
